// Mixer building blocks: the per-oscillator drive (waveshaper) stage and the
// curve that powers it. Used per-osc inside each Voice and once on the master
// bus. The drive curve blends a clean linear response (amount 0) into a
// normalized tanh soft-clip (amount 1), so drive at 0 is transparent.

const DRIVE_K = 9; // tanh steepness at full drive
const CURVE_SAMPLES = 1024;

/** Build a waveshaper transfer curve for a drive amount in [0, 1]. */
export function makeDriveCurve(amount: number, samples = CURVE_SAMPLES): Float32Array {
  const a = Math.min(1, Math.max(0, amount));
  const k = 1 + a * DRIVE_K;
  const norm = Math.tanh(k);
  const curve = new Float32Array(samples);
  for (let i = 0; i < samples; i++) {
    const x = (i / (samples - 1)) * 2 - 1;
    const shaped = Math.tanh(k * x) / norm;
    curve[i] = (1 - a) * x + a * shaped;
  }
  return curve;
}

/** A single waveshaper drive stage; `node` is both its input and output. */
export class DriveStage {
  readonly node: WaveShaperNode;

  constructor(ctx: BaseAudioContext) {
    this.node = ctx.createWaveShaper();
    this.node.oversample = '2x';
    this.setDrive(0);
  }

  setDrive(amount: number): void {
    // Cast: lib.dom types `curve` as Float32Array<ArrayBuffer>; our array is
    // backed by a plain ArrayBuffer but inferred as ArrayBufferLike.
    this.node.curve = makeDriveCurve(amount) as Float32Array<ArrayBuffer>;
  }
}
